import React, { useEffect, useRef, useState } from 'react';
import { useGamingStore, McProfile } from '../../store/gamingStore';
import { ArrowLeft, Plus, Play, Settings, Copy, Trash2, Clock, Server, Package, Loader2 } from 'lucide-react';
import ProfileDetail from './ProfileDetail';
import CreateProfileModal from './CreateProfileModal';
import MicrosoftLoginModal from '../settings/MicrosoftLoginModal';

const LOADER_COLORS: Record<string, string> = {
  vanilla: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  forge: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  fabric: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  quilt: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

const ProfileCard: React.FC<{ profile: McProfile }> = ({ profile }) => {
  const { openProfile, launchProfile, isLaunchingProfile, duplicateProfile, deleteProfile } = useGamingStore();
  const isLaunching = isLaunchingProfile === profile.id;
  const [showMenu, setShowMenu] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const loaderStyle = LOADER_COLORS[profile.minecraft.loader] || LOADER_COLORS.vanilla;
  const modCount = profile.mods?.length || 0;
  const hasVideoBackground = !!profile.video_background_url;
  const hasImageBackground = !!profile.banner_url;
  const hasMediaBackground = hasVideoBackground || hasImageBackground;
  const lastPlayed = profile.last_played_at
    ? new Date(profile.last_played_at).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' })
    : null;

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      void videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      className="group relative bg-bg-secondary border border-border-color rounded-xl overflow-hidden hover:border-gaming-accent/50 transition-all duration-200 cursor-pointer"
      onClick={() => openProfile(profile.id)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Top banner / icon area */}
      <div className="h-24 bg-gradient-to-br from-emerald-900/40 to-bg-tertiary flex items-center justify-center relative overflow-hidden">
        {hasMediaBackground && (
          <>
            {hasImageBackground && (
              <img src={profile.banner_url || ''} alt="" className="absolute inset-0 w-full h-full object-cover" />
            )}
            {hasVideoBackground && (
              <video
                ref={videoRef}
                src={profile.video_background_url || ''}
                muted
                loop
                playsInline
                preload="metadata"
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-black/25" />
          </>
        )}

        {!hasMediaBackground && (profile.icon ? (
          <img src={profile.icon} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-14 h-14 rounded-xl bg-emerald-600/30 border border-emerald-500/30 flex items-center justify-center">
            <span className="text-2xl font-black text-emerald-400">
              {profile.name.charAt(0).toUpperCase()}
            </span>
          </div>
        ))}
        {/* Source badge */}
        {profile.source === 'server' && (
          <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gaming-accent/20 text-gaming-accent border border-gaming-accent/30">
            <Server className="w-3 h-3 inline mr-0.5" />
            Serveur
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        <h3 className="font-bold text-text-primary text-sm truncate">{profile.name}</h3>
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
          <span className="text-[11px] text-text-secondary">{profile.minecraft.version}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${loaderStyle}`}>
            {profile.minecraft.loader}
          </span>
          {modCount > 0 && (
            <span className="text-[10px] text-text-muted flex items-center gap-0.5">
              <Package className="w-3 h-3" />{modCount}
            </span>
          )}
        </div>

        {/* Server address */}
        {profile.server?.address && (
          <p className="text-[10px] text-text-muted mt-1 truncate">{profile.server.address}:{profile.server.port}</p>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-1 text-[10px] text-text-muted">
            {lastPlayed && (
              <>
                <Clock className="w-3 h-3" />
                {lastPlayed}
              </>
            )}
          </div>

          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {/* Context menu */}
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-1 rounded hover:bg-bg-tertiary text-text-muted hover:text-text-secondary transition-colors opacity-0 group-hover:opacity-100"
              >
                <Settings className="w-3.5 h-3.5" />
              </button>
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 bottom-full mb-1 w-36 bg-bg-tertiary border border-border-color rounded-lg shadow-xl z-20 py-1">
                    <button
                      onClick={() => { duplicateProfile(profile.id); setShowMenu(false); }}
                      className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary hover:bg-bg-secondary transition-colors"
                    >
                      <Copy className="w-3.5 h-3.5" /> Dupliquer
                    </button>
                    <button
                      onClick={() => { deleteProfile(profile.id); setShowMenu(false); }}
                      className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-red-400 hover:text-red-300 hover:bg-bg-secondary transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Supprimer
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Launch button */}
            <button
              onClick={() => launchProfile(profile.id)}
              disabled={isLaunching}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gaming-accent text-white hover:bg-gaming-accent-hover transition-colors disabled:opacity-50 shadow-sm"
              aria-label={`Lancer le profil ${profile.name}`}
              title="Lancer le profil"
            >
              {isLaunching ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Play className="w-3.5 h-3.5 fill-current" />
              )}
              <span className="text-[11px] font-semibold leading-none">Jouer</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MinecraftUniverse: React.FC = () => {
  const {
    profiles, fetchProfiles, isLoadingProfiles, selectedProfileId, goBackToHub,
    showMicrosoftLoginModal, setShowMicrosoftLoginModal, onMicrosoftLoginSuccess,
  } = useGamingStore();
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  // If a profile is selected, show its detail view (modal must still render)
  if (selectedProfileId) {
    return (
      <>
        <ProfileDetail />
        {showMicrosoftLoginModal && (
          <MicrosoftLoginModal
            onSuccess={onMicrosoftLoginSuccess}
            onClose={() => setShowMicrosoftLoginModal(false)}
          />
        )}
      </>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border-color bg-bg-secondary/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={goBackToHub}
                className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Hub
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center">
                  <span className="text-sm font-black text-emerald-400">M</span>
                </div>
                <div>
                  <h1 className="text-lg font-black text-text-primary leading-tight">Minecraft</h1>
                  <p className="text-[10px] text-emerald-400 uppercase tracking-wider font-semibold">
                    {profiles.length} profil{profiles.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gaming-accent text-white text-sm font-medium hover:bg-gaming-accent-hover transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nouveau profil
            </button>
          </div>
        </div>
      </div>

      {/* Profile grid */}
      <div className="flex-1 overflow-y-auto gaming-scrollbar">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {isLoadingProfiles ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-gaming-accent animate-spin" />
            </div>
          ) : profiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-20 h-20 rounded-2xl bg-emerald-600/10 border border-emerald-500/20 flex items-center justify-center mb-4">
                <span className="text-4xl font-black text-emerald-400/50">M</span>
              </div>
              <h2 className="text-lg font-bold text-text-primary mb-1">Aucun profil</h2>
              <p className="text-sm text-text-secondary mb-4 max-w-sm">
                Créez votre premier profil Minecraft pour commencer. Chaque profil est une configuration isolée avec ses propres mods, shaders et resource packs.
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gaming-accent text-white text-sm font-medium hover:bg-gaming-accent-hover transition-colors"
              >
                <Plus className="w-4 h-4" />
                Créer un profil
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {profiles.map(profile => (
                <ProfileCard key={profile.id} profile={profile} />
              ))}
              {/* Add new profile card */}
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex flex-col items-center justify-center min-h-[200px] border-2 border-dashed border-border-color rounded-xl hover:border-gaming-accent/50 hover:bg-gaming-accent/5 transition-all duration-200 group"
              >
                <Plus className="w-8 h-8 text-text-muted group-hover:text-gaming-accent transition-colors" />
                <span className="text-xs text-text-muted group-hover:text-text-secondary mt-2 transition-colors">
                  Nouveau profil
                </span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create Profile Modal */}
      {showCreateModal && (
        <CreateProfileModal onClose={() => setShowCreateModal(false)} />
      )}

      {/* Microsoft Login Modal (shown when launching without a saved account) */}
      {showMicrosoftLoginModal && (
        <MicrosoftLoginModal
          onSuccess={onMicrosoftLoginSuccess}
          onClose={() => setShowMicrosoftLoginModal(false)}
        />
      )}
    </div>
  );
};

export default MinecraftUniverse;
